import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useTexte, WORTLAUT } from '../texte'
import { useEscape } from '../hooks/useEscape'

/**
 * Die eigene Rückfrage — statt `window.confirm`.
 *
 * Der Browserdialog kann den zerstörenden Knopf weder rot machen noch beim
 * Namen nennen: er heißt immer „OK", egal ob dahinter ein Löschen steht.
 * Hier heißt der Knopf, was er tut („Löschen", „Wegnehmen"), trägt die
 * Warnfarbe, und **Abbrechen ist die sichere Vorgabe** — Escape und ein
 * Tipp auf den Hintergrund sagen Nein.
 *
 * Benutzt wie `confirm`: `if (await rueckfrage('Diese Nachricht löschen?',
 * 'Löschen')) …`. Der `RueckfrageHost` steht einmal in der `Shell`; sollte
 * er je fehlen, fällt die Funktion auf `window.confirm` zurück — eine
 * hässliche Rückfrage ist besser als ein stilles Ja.
 */
/**
 * More than a question (1.113.0, the sheet before a link opens): lines under
 * it, warnings in their colour, a button that is not dangerous, and a call
 * **inside the tap**.
 *
 * `beimJa` runs synchronously in the button's click, before the promise
 * settles. A `window.open()` after `await rueckfrage(…)` would run a
 * microtask later -- Chromium counts that as the tap, Safari does not
 * promise to (the same rule as for passkeys in CLAUDE.md).
 */
export interface Zusatz {
  details?: string[]
  /** The warnings, each one line; `data-warnung` carries its key. */
  warnungen?: { schluessel: string; text: string }[]
  /** `'normal'`: the button does not carry the colour of danger. */
  art?: 'gefahr' | 'normal'
  beimJa?: () => void
  /** A mark for the prüfstand on the sheet. */
  merkmal?: string
}

interface Frage extends Zusatz {
  frage: string
  knopf: string
  antwort: (ja: boolean) => void
}

let stellen: ((frage: Frage) => void) | null = null

export function rueckfrage(frage: string, knopf: string = WORTLAUT.allgemein.ja, zusatz: Zusatz = {}): Promise<boolean> {
  if (stellen === null) {
    const ja = window.confirm([frage, ...(zusatz.details ?? []), ...(zusatz.warnungen ?? []).map((w) => w.text)].join('\n'))
    if (ja) zusatz.beimJa?.()
    return Promise.resolve(ja)
  }
  const fragen = stellen
  return new Promise((antwort) => fragen({ frage, knopf, antwort, ...zusatz }))
}

export function RueckfrageHost() {
  const t = useTexte().allgemein
  const [offen, setOffen] = useState<Frage | null>(null)

  useEffect(() => {
    stellen = (frage) => setOffen(frage)
    return () => {
      stellen = null
    }
  }, [])

  // Enter bleibt bewusst unbelegt: die gefährliche Antwort soll ein
  // gezielter Tipp sein, kein Durchwinken.
  useEscape(offen === null ? null : () => beantworten(false))

  if (offen === null) return null

  function beantworten(ja: boolean) {
    if (ja) offen?.beimJa?.()
    offen?.antwort(ja)
    setOffen(null)
  }

  return createPortal(
    <div
      className="rueckfrage"
      role="alertdialog"
      aria-modal="true"
      aria-label={offen.frage}
      onClick={() => beantworten(false)}
    >
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()} data-rueckfrage={offen.merkmal}>
        <p className="rueckfrage__frage">{offen.frage}</p>
        {offen.details?.map((zeile, i) => (
          <p key={`d${i}`} className="rueckfrage__detail">{zeile}</p>
        ))}
        {offen.warnungen !== undefined && offen.warnungen.length > 0 && (
          <ul className="rueckfrage__warnungen">
            {offen.warnungen.map((w) => (
              <li key={w.schluessel} data-warnung={w.schluessel}>{w.text}</li>
            ))}
          </ul>
        )}
        <div className="rueckfrage__knoepfe">
          <button
            type="button"
            className="button button--outline button--small"
            onClick={() => beantworten(false)}
            // Der sichere Weg bekommt den Fokus -- wer blind Enter drückt,
            // löscht nichts.
            autoFocus
          >
            {t.abbrechen}
          </button>
          <button
            type="button"
            className={offen.art === 'normal' ? 'button button--small' : 'button button--small rueckfrage__gefahr'}
            onClick={() => beantworten(true)}
            data-rueckfrage-ja=""
          >
            {offen.knopf}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
